import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { LifeBuoy, ExternalLink, Loader2, MessageSquare, Ticket, Paperclip, RefreshCw, MessageCircle, Building2, X, ChevronsUpDown, Check, Search } from "lucide-react";
import DOMPurify from "dompurify";
import { useSearchParams } from "react-router-dom";
import Intercom, { shutdown, show } from "@intercom/messenger-js-sdk";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";

const fmtWhen = (ts) => (ts ? new Date(ts * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const STATE_TONE = { open: "bg-emerald-50 text-emerald-700 border-emerald-200", closed: "bg-slate-100 text-slate-500 border-slate-200", snoozed: "bg-amber-50 text-amber-700 border-amber-200" };
const CHANNEL_LABEL = { email: "Email", conversation: "Chat", phone_call: "Phone", "phone-call": "Phone" };

const StateBadge = ({ state }) => <Badge variant="outline" className={`text-[10px] capitalize ${STATE_TONE[state] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{state}</Badge>;

const ConversationRow = ({ c, onOpen, onPickPartner }) => (
  <button onClick={() => onOpen(c.id)} data-testid={`sd-conv-item-${c.id}`}
    className="flex w-full items-start gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-slate-50/60">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
      {(c.author_name || "?").slice(0, 1).toUpperCase()}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium text-slate-800">{c.author_name}</span>
        <span className="truncate text-[11px] text-slate-400">{c.author_email}</span>
        {c.partner_name && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onPickPartner?.(c.partner_org_id); }}
            data-testid={`sd-conv-partner-${c.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 transition-colors hover:border-indigo-400 hover:bg-indigo-100">
            <Building2 className="h-3 w-3" /> {c.partner_name}
          </button>
        )}
      </div>
      <div className="truncate text-sm text-slate-700">{c.title}</div>
      <div className="truncate text-xs text-slate-400">{c.preview}</div>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-1">
      <span className="text-[11px] text-slate-400">{fmtWhen(c.updated_at)}</span>
      <div className="flex items-center gap-1">
        {c.attachments > 0 && <Paperclip className="h-3 w-3 text-slate-400" />}
        {c.sla_status === "missed" && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-600 border-rose-200">SLA missed</Badge>}
        <StateBadge state={c.state} />
      </div>
      {c.assignee && <span className="text-[10px] text-slate-400">→ {c.assignee}</span>}
    </div>
  </button>
);

const MessageBubble = ({ m }) => {
  const isTeam = m.author_type === "admin" || m.author_type === "bot";
  return (
    <div className={`flex ${isTeam ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[92%] rounded-lg border px-3 py-2 ${isTeam ? "border-primary/20 bg-primary/5" : "border-slate-100 bg-white"}`}>
        <div className="mb-1 flex items-center gap-2 text-[11px] text-slate-400">
          <span className="font-medium text-slate-600">{m.author_name}</span>
          <span>{fmtWhen(m.created_at)}</span>
        </div>
        {/* Iter 90k — enforce max-width + word-wrap on rich email HTML so
            wide fixed-width tables (RBC PayEdge etc.) don't overflow the
            tray and clip the right column. */}
        {m.body && <div className="prose prose-sm max-w-none text-sm text-slate-700 [&_a]:text-primary [&_p]:my-1 [&_img]:max-w-full [&_img]:h-auto [&_table]:!w-full [&_table]:!max-w-full [&_table]:!table-auto [&_table]:break-words [&_td]:break-words [&_th]:break-words overflow-x-auto"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body) }} />}
        {m.attachments.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noreferrer"
            className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline">
            <Paperclip className="h-3 w-3" /> {a.name}
          </a>
        ))}
      </div>
    </div>
  );
};

export default function ServiceDesk() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bootInfo, setBootInfo] = useState(null);
  const [tab, setTab] = useState("conversations");
  const [stateFilter, setStateFilter] = usePersistedState("service-desk.state", "open");
  const [partnerFilter, setPartnerFilter] = usePersistedState("service-desk.partner", "", searchParams.get("partner"));
  const [partnerSummary, setPartnerSummary] = useState({ partners: [], unmatched: 0, scanned: 0 });
  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [convCursor, setConvCursor] = useState("");
  const [convTotal, setConvTotal] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [ticketCursor, setTicketCursor] = useState("");
  const [loading, setLoading] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    let booted = false;
    api.get("/intercom/boot").then(({ data }) => {
      setBootInfo(data);
      const cfg = { app_id: data.app_id, vertical_padding: 90 };
      if (data.intercom_user_jwt) cfg.intercom_user_jwt = data.intercom_user_jwt;
      Intercom(cfg);
      booted = true;
    }).catch(() => setBootInfo({ app_id: "", identified: false }));
    return () => { if (booted) shutdown(); };
  }, []);

  useEffect(() => {
    api.get(`/intercom/partner-summary?state=${stateFilter}`)
      .then(({ data }) => setPartnerSummary(data || { partners: [] }))
      .catch(() => {});
  }, [stateFilter]);

  const partnerQ = partnerFilter ? `&partner=${encodeURIComponent(partnerFilter)}` : "";

  const loadConvs = async (filter, cursor = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/intercom/conversations?state=${filter}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}${partnerQ}`);
      setConvs((prev) => (cursor ? [...prev, ...data.conversations] : data.conversations));
      setConvCursor(data.next_cursor || "");
      setConvTotal(data.total_count || 0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load conversations"); }
    finally { setLoading(false); }
  };

  const loadTickets = async (cursor = "") => {
    setLoading(true);
    try {
      const partnerP = partnerFilter ? `${cursor ? "&" : "?"}partner=${encodeURIComponent(partnerFilter)}` : "";
      const { data } = await api.get(`/intercom/tickets${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}${partnerP}`);
      setTickets((prev) => (cursor ? [...prev, ...data.tickets] : data.tickets));
      setTicketCursor(data.next_cursor || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load tickets"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadConvs(stateFilter); }, [stateFilter, partnerFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "tickets") { setTickets([]); loadTickets(); }
  }, [tab, partnerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickPartner = (org_id) => {
    setPartnerFilter(org_id);
    const next = new URLSearchParams(searchParams);
    if (org_id) next.set("partner", org_id); else next.delete("partner");
    setSearchParams(next, { replace: true });
  };

  const activePartner = useMemo(() => {
    if (!partnerFilter) return null;
    const fromSummary = partnerSummary.partners.find((p) => p.org_id === partnerFilter);
    if (fromSummary) return fromSummary;
    // Fallback while the summary is loading (or the partner isn't in it):
    // derive the partner name from the first matching row we already have.
    const fromRow = [...convs, ...tickets].find((r) => r.partner_org_id === partnerFilter);
    return fromRow ? { org_id: partnerFilter, name: fromRow.partner_name, count: null } : { org_id: partnerFilter, name: partnerFilter, count: null };
  }, [partnerFilter, partnerSummary, convs, tickets]);

  const openThread = async (id) => {
    setThreadLoading(true); setThread({ id });
    try { const { data } = await api.get(`/intercom/conversations/${id}`); setThread(data); }
    catch { toast.error("Could not open conversation"); setThread(null); }
    finally { setThreadLoading(false); }
  };

  const inboxUrl = bootInfo?.app_id ? `https://app.intercom.com/a/inbox/${bootInfo.app_id}` : "https://app.intercom.com/";

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-1" data-testid="service-desk-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900"><LifeBuoy className="h-6 w-6 text-primary" /> Service Desk</h1>
          <p className="mt-1 text-sm text-slate-500">Live view of your Intercom workspace — conversations, tickets and chat.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => show()} data-testid="sd-chat-button"><MessageCircle className="h-4 w-4" /> Chat with us</Button>
          <a href={inboxUrl} target="_blank" rel="noreferrer" data-testid="sd-open-intercom">
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><ExternalLink className="h-4 w-4" /> Open Intercom</Button>
          </a>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="conversations" data-testid="sd-tab-conversations"><MessageSquare className="mr-1 h-4 w-4" /> Conversations</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="sd-tab-tickets"><Ticket className="mr-1 h-4 w-4" /> Tickets</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap items-center gap-1.5">
            <Popover open={partnerPickerOpen} onOpenChange={setPartnerPickerOpen}>
              <PopoverTrigger asChild>
                <button data-testid="sd-partner-filter"
                  className="inline-flex h-8 w-[260px] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700 shadow-sm transition-colors hover:border-slate-300">
                  <span className="flex items-center gap-1.5 truncate">
                    <Search className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate">
                      {activePartner
                        ? activePartner.name
                        : `All partners${partnerSummary.scanned ? ` · ${partnerSummary.scanned} ${stateFilter === "all" ? "" : stateFilter}` : ""}`}
                    </span>
                  </span>
                  <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="end" data-testid="sd-partner-picker">
                <Command>
                  <CommandInput placeholder="Search partners…" data-testid="sd-partner-search" />
                  <CommandList className="max-h-72">
                    <CommandEmpty>No partners match.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="__all__" onSelect={() => { pickPartner(""); setPartnerPickerOpen(false); }}
                        data-testid="sd-partner-opt-all">
                        <Check className={`mr-2 h-4 w-4 ${!partnerFilter ? "opacity-100" : "opacity-0"}`} />
                        <span className="flex-1">All partners</span>
                        {partnerSummary.scanned > 0 && (
                          <span className="text-[11px] text-slate-400">{partnerSummary.scanned} {stateFilter === "all" ? "total" : stateFilter}</span>
                        )}
                      </CommandItem>
                      {partnerSummary.partners.map((p) => (
                        <CommandItem key={p.org_id} value={`${p.name} ${p.org_id}`}
                          onSelect={() => { pickPartner(p.org_id); setPartnerPickerOpen(false); }}
                          data-testid={`sd-partner-opt-${p.org_id}`}>
                          <Check className={`mr-2 h-4 w-4 ${partnerFilter === p.org_id ? "opacity-100" : "opacity-0"}`} />
                          <span className="flex-1 truncate">{p.name}</span>
                          <span className="text-[11px] font-medium text-slate-500">{p.count}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {tab === "conversations" && ["open", "closed", "all"].map((f) => (
              <button key={f} onClick={() => setStateFilter(f)} data-testid={`sd-filter-${f}`}
                className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${stateFilter === f ? "border-primary bg-primary/10 text-primary" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                {f}
              </button>
            ))}
            {tab === "conversations" && (
              <Button variant="ghost" size="sm" onClick={() => loadConvs(stateFilter)} disabled={loading} data-testid="sd-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
            )}
          </div>
        </div>

        {activePartner && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs text-indigo-800" data-testid="sd-partner-active">
            <Building2 className="h-3.5 w-3.5" />
            <span>
              Filtering by <b>{activePartner.name}</b>
              {typeof activePartner.count === "number" && ` · ${activePartner.count} ${stateFilter === "all" ? "" : stateFilter}`}
            </span>
            <button onClick={() => pickPartner("")} data-testid="sd-partner-clear"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:border-indigo-500 hover:bg-indigo-100">
              <X className="h-3 w-3" /> Clear
            </button>
          </div>
        )}

        <TabsContent value="conversations" className="mt-3">
          <Card className="p-3">
            {convTotal > 0 && <p className="mb-2 px-1 text-[11px] text-slate-400" data-testid="sd-conv-total">{convTotal.toLocaleString()} conversation(s) · showing {convs.length}</p>}
            {loading && convs.length === 0 ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : convs.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No {stateFilter !== "all" ? stateFilter : ""} conversations.</p>
            ) : (
              <div className="space-y-1.5">
                {convs.map((c) => <ConversationRow key={c.id} c={c} onOpen={openThread} onPickPartner={pickPartner} />)}
                {convCursor && (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => loadConvs(stateFilter, convCursor)} disabled={loading} data-testid="sd-load-more">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
                  </Button>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="tickets" className="mt-3">
          <Card className="p-3">
            {loading && tickets.length === 0 ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : tickets.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">No tickets.</p>
            ) : (
              <div className="space-y-1.5">
                {tickets.map((t) => (
                  <div key={t.id} className="flex items-start gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2.5" data-testid={`sd-ticket-row-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">#{t.ticket_id}</span>
                        {t.type && <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">{t.type}</Badge>}
                        {t.partner_name && (
                          <button type="button" onClick={() => pickPartner(t.partner_org_id)}
                            data-testid={`sd-ticket-partner-${t.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 transition-colors hover:border-indigo-400 hover:bg-indigo-100">
                            <Building2 className="h-3 w-3" /> {t.partner_name}
                          </button>
                        )}
                      </div>
                      <div className="truncate text-sm font-medium text-slate-800">{t.title}</div>
                      {t.description && <div className="truncate text-xs text-slate-400">{t.description}</div>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-[11px] text-slate-400">{fmtWhen(t.updated_at)}</span>
                      <Badge variant="outline" className={`text-[10px] capitalize ${t.open ? STATE_TONE.open : STATE_TONE.closed}`}>{(t.state || "").replaceAll("_", " ")}</Badge>
                      {t.assignee && <span className="text-[10px] text-slate-400">→ {t.assignee}</span>}
                    </div>
                  </div>
                ))}
                {ticketCursor && (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => loadTickets(ticketCursor)} disabled={loading} data-testid="sd-tickets-load-more">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
                  </Button>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Sheet open={!!thread} onOpenChange={(o) => !o && setThread(null)}>
        {/* Iter 90k — 50% wider tray so email previews with rendered
            tables (e.g. RBC PayEdge order details) fit without the
            right column getting clipped. Was `sm:max-w-xl` (36rem);
            bumped to a clamped 54rem so on narrower monitors it still
            respects the viewport. */}
        <SheetContent className="w-full overflow-y-auto sm:max-w-[54rem]" data-testid="sd-conv-sheet" aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 pr-6 text-base">
              {thread?.state && <StateBadge state={thread.state} />}
              <span className="truncate">{thread?.title || "Conversation"}</span>
            </SheetTitle>
          </SheetHeader>
          {threadLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="mt-4 space-y-3">
              {(thread?.messages || []).map((m, i) => <MessageBubble key={i} m={m} />)}
              {thread?.id && (
                <a href={`${inboxUrl}/inbox/conversation/${thread.id}`} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-1 pt-2 text-xs text-primary hover:underline" data-testid="sd-open-in-intercom">
                  <ExternalLink className="h-3 w-3" /> Open Ticket in new tab
                </a>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
